<template>
    <div class="ack-information-activity tw:flex tw:flex-col tw:gap-6">
        <e-field>
            <display-content :content="activityContent.content" />
        </e-field>

        <div class="ack-information-acknowledgment">
            <p class="ack-information-acknowledgment__title tw:font-semibold tw:text-lg">Acknowledgment</p>
            <e-checkbox v-model="userAccepted" :disabled="acknowledgementDisabled">
                {{ activityContent.acknowledgment_message }}
            </e-checkbox>
        </div>

        <e-field v-if="allow_student_attachments && !completed" label="File Upload">
            <edverum-file-upload
                v-if="!completed"
                :autosave="false"
                :attachments="media"
                :upload-context="uploadContext"
                @add="onMediaAdd"
                @delete="onMediaDelete"
            />
        </e-field>

        <activity-completion-action-row>
            <e-tooltip
                position="is-top"
                :active="submitBlockedByExistingGrade"
                :label="translate('activity_graded_no_submissions_allowed')"
                multilined
            >
                <e-button
                    v-if="canShowSubmit"
                    :loading="isLoading"
                    :disabled="disabled"
                    type="is-primary"
                    @click="submit"
                    @keyup="submit"
                >
                    Submit
                </e-button>
            </e-tooltip>
        </activity-completion-action-row>

        <!-- The divider always renders on desktop/tablet (as before 868kj17g6); on mobile it is
             hidden when no submissions list follows it. -->
        <Divider
            class="ack-information-submissions-divider"
            :class="{ 'ack-information-submissions-divider--standalone': readOnly || !canViewSubmissions }"
        />
        <submissions
            v-if="!readOnly && canViewSubmissions"
            :namespace="namespace"
            @fetch-submissions="toggleSubmissionLoading"
        >
            <template #content="{ submission }">
                <attachments :media="submission.submittable.media" />
            </template>
        </submissions>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import some from 'lodash/some.js';
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import EdverumFileUpload from '@/components/file-upload/EdverumFileUpload.vue';
import { useActivity } from '@/composables/useActivity.js';
import { FILE_UPLOAD_CONTEXT } from '@/constants/fileUploadRejection.js';

export default {
    name: 'AcknowledgeInformation',

    components: {
        ActivityCompletionActionRow,
        Attachments,
        EdverumFileUpload,
        DisplayContent,
        Submissions,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        // `media` (this component's own mutable upload list) and `disabled` (its own
        // acknowledgment-aware formula) stay component-owned — drop them from the spread.
        const { media, disabled, ...api } = useActivity(props, ctx, {
            getPayload: () => instance.proxy.payload,
            uploadContext: FILE_UPLOAD_CONTEXT.ACKNOWLEDGE_INFORMATION_STUDENT,
        });

        return { translate, ...api };
    },

    data: () => ({
        userAccepted: null,
        media: [],
        uploadContext: FILE_UPLOAD_CONTEXT.ACKNOWLEDGE_INFORMATION_STUDENT,
    }),

    computed: {
        acknowledgementDisabled() {
            if (this.usedAllAttemptAuthorizations) {
                return true;
            }

            return (
                !this.userHasAuthorizedAttempt &&
                (!!this.completed || this.readOnly || !this.hasPermissionToSubmit || this.hasExistingGrade)
            );
        },

        disabled() {
            if (this.submitBlockedByExistingGrade) {
                return true;
            }

            if (this.usedAllAttemptAuthorizations) {
                return true;
            }

            return some([!this.userAccepted, this.isLate && !this.canSubmitLate && !this.userHasAuthorizedAttempt]);
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: {
                    acknowledgment: this.userAccepted,
                    media: this.media,
                },
            };
        },

        allow_student_attachments() {
            return get(this.activity, 'activity_settings.allow_student_attachments', false);
        },
    },

    watch: {
        // Immediate so a reopen of an already-completed activity ticks the checkbox on mount.
        // Without it, completed is true before the watcher runs and the box stays empty + disabled.
        completed: {
            immediate: true,
            handler(completed) {
                if (completed) {
                    this.userAccepted = true;
                }
            },
        },

        hasExistingGrade: {
            immediate: true,
            handler(hasGrade) {
                if (hasGrade) {
                    this.userAccepted = true;
                }
            },
        },
    },

    methods: {
        onMediaAdd(files) {
            this.media = files;
        },

        onMediaDelete(index) {
            this.media.splice(index, 1);
        },
    },
};
</script>

<style scoped lang="scss">
@import 'resources/scss/toolbox.scss';

.ack-information-activity {
    > * {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-completion-action-row) {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-submissions-section) {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
    }
}

.ack-information-acknowledgment__title {
    margin-bottom: 0.5rem;
}

@media screen and (max-width: 768px) {
    .ack-information-submissions-divider.p-divider {
        margin: 1rem 0 0;
    }

    .ack-information-submissions-divider--standalone {
        display: none;
    }
}

// Desktop/tablet (>= 769px) must render exactly as before the mobile spacing work
// (868kj17g6 AC-6, ADR-051 decision 5). The previous markup stacked Bulma `.columns`
// (1.5rem rhythm, $column-gap padding), so only the spots where that rhythm differs from
// the flat 1.5rem gap are corrected here; mobile is unchanged.
@media screen and (min-width: 769px) {
    // The acknowledgment used `<e-field label>`: a `.control` wrapper ($size-normal text)
    // with no gap under the label.
    .ack-information-acknowledgment {
        font-size: $size-normal;
    }

    .ack-information-acknowledgment__title {
        margin-bottom: 0;
    }

    // Last `.column` padding plus the row's own `tw:mt-4` (it out-collapses the
    // `.columns` bottom margin): $column-gap + 1rem above the action row.
    .ack-information-activity > .activity-completion-action-row {
        margin-top: calc(#{$column-gap} + 1rem - 1.5rem) !important;
    }

    // PrimeVue Divider's own 1rem vertical margins (the following `.columns` top margin and
    // `.column` padding cancel out), instead of the 1.5rem gap; trailing 1rem when it is last.
    .ack-information-activity > .ack-information-submissions-divider {
        margin-top: calc(1rem - 1.5rem) !important;
        margin-bottom: calc(1rem - 1.5rem) !important;
    }

    .ack-information-activity > .ack-information-submissions-divider:last-child {
        margin-bottom: 1rem !important;
    }
}
</style>

<style lang="scss">
.tags {
    margin-bottom: 0;
}
</style>
